import { createContext, useContext, type ReactNode } from 'react';

import type { BlockDocument } from './BlockRenderer';

/**
 * The global sections a page resolved, keyed by id.
 *
 * ── THE PAGE BRINGS THEM; THE BLOCK DOES NOT FETCH THEM ──────────────────────────────
 *
 * §52/§53. The resolver bundles every referenced section into the one CMS response, and
 * the GlobalSection block reads its own from here. A block that fetched itself would make
 * a page with a footer and a sponsor strip cost three round trips instead of one, and the
 * count would grow with the page.
 *
 * It is also the same rule the event context follows, for the same reason: a block that
 * can fetch by id is a block a document can point at somebody else's content.
 */
export interface ResolvedGlobalSection {
  id: string;
  sectionType: string;
  version: number;
  document: BlockDocument;
}

export type GlobalSectionMap = Record<string, ResolvedGlobalSection>;

const GlobalSectionContext = createContext<GlobalSectionMap>({});

export function GlobalSectionProvider({
  sections, children,
}: { sections: GlobalSectionMap | null | undefined; children: ReactNode }) {
  return (
    <GlobalSectionContext.Provider value={sections ?? {}}>
      {children}
    </GlobalSectionContext.Provider>
  );
}

/**
 * Look one up.
 *
 * Object.hasOwn rather than a bare index: the map arrives as JSON from the network, and
 * `sections['constructor']` would otherwise return a function that the renderer would
 * then try to treat as a document.
 */
export function useGlobalSection(id: string | null | undefined): ResolvedGlobalSection | null {
  const map = useContext(GlobalSectionContext);
  if (!id || !map || !Object.hasOwn(map, id)) return null;
  const found = map[id];
  return found && typeof found === 'object' && found.document ? found : null;
}
